import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AbortJobModal from "../Jobs/AbortJobModal";
import DeletionStatusList from "../Jobs/DeletionStatusList";
import { isJobFinished } from "../../models/Jobs/JobActions";
import JobsRepository from "../../repositories/JobsRepository";
import { JobRequestStatus } from "../../models/Jobs/JobRequestStatus";
import { DeleteTemplateFilesRequest, JobRequest } from "../../models/Jobs/JobRequest";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";

interface DeleteTemplateFilesStatusTableProps {
  templateId?: string;
}

export const DeleteTemplateFilesStatusTable: React.FC<DeleteTemplateFilesStatusTableProps> = ({
  templateId,
}) => {
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [templateFilesDeletionStatus, setTemplateFilesDeletionStatus] = useState<DeleteTemplateFilesRequest[] | null>(null);

  const statusQuery = useQuery({
    queryKey: [QUERY_KEYS.DELETE_TEMPLATE_FILES, templateId],
    queryFn: async () => await jobsRepository.getTemplateFilesDeletionStatus(templateId),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: (query) =>
      query.state.data?.some((job) => !isJobFinished(job)) ? 30_000 : QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (statusQuery.data) setTemplateFilesDeletionStatus(statusQuery.data);
  }, [statusQuery]);

  const notifiedJobIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!statusQuery.data) return;
    const terminal = new Set([JobRequestStatus.Completed, JobRequestStatus.Failed]);
    const newlyTerminal = statusQuery.data.filter(
      (j) => terminal.has(j.status) && !notifiedJobIdsRef.current.has(j.id),
    );
    if (newlyTerminal.length === 0) return;
    newlyTerminal.forEach((j) => notifiedJobIdsRef.current.add(j.id));
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATE_FILES, templateId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATE, templateId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
  }, [statusQuery.data, templateId, queryClient]);

  return (
    <>
      <DeletionStatusList
        jobs={templateFilesDeletionStatus ?? []}
        loading={statusQuery.isFetching || statusQuery.isError}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        onAbort={setAbortJob}
        onStopped={() => queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_TEMPLATE_FILES, templateId] })}
      />
      {abortJob && (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.DELETE_TEMPLATE_FILES, templateId] });
            if (abortJob.status !== JobRequestStatus.Completed) {
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATE_FILES, templateId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATE, templateId] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEMPLATES] });
              await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION] });
            }
            setAbortJob(null);
          }}
        />
      )}
    </>
  );
};
